import { GREAT_WHITE, SLINKY } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H6 } from 'UIComponents/elements/headings';
import UITab from 'UIComponents/nav/UITab';
import UITabs from 'UIComponents/nav/UITabs';
import UITable from 'UIComponents/table/UITable';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { API_NO_ACCESS } from 'self-service-api/constants/FreeUserAccessValues';
import { ENTERPRISE, FREE, PROFESSIONAL, STARTER, currentOrderedProductLevels } from 'self-service-api/constants/ProductLevels';
import { SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { ProductPageContext } from '../../../context/ProductPageContext';
import { getFeaturesByProductLineSelector } from '../../../selectors/FeatureSelectors';
import { transformFeatureRecord } from '../../../utils/translatedFeatureUtils';
import TranslatedFeature from '../../feature-table/TranslatedFeature';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PaidUsersFeatureTable = ({
  productLine,
  productLevel,
  hideFreeUsersDisclaimer
}) => {
  const {
    highlightedProducts
  } = useContext(ProductPageContext);
  const features = useSelector(state => {
    return getFeaturesByProductLineSelector(state, {
      productLine
    });
  });
  const makeFeatureCells = ({
    featureDoubles,
    apiName
  }) => {
    return featureDoubles.map(featureCouple => {
      return /*#__PURE__*/_jsxs("tr", {
        children: [featureCouple.map((feature, index) => {
          const translated = transformFeatureRecord({
            feature,
            apiName
          });
          return /*#__PURE__*/_jsx("td", {
            style: index === 0 ? {
              borderRight: `1px solid ${GREAT_WHITE}`
            } : {},
            children: /*#__PURE__*/_jsx(UITooltip, {
              title: /*#__PURE__*/_jsx(TranslatedFeature, {
                translated: translated,
                attribute: "description"
              }),
              children: /*#__PURE__*/_jsx("span", {
                style: {
                  cursor: 'help'
                },
                children: /*#__PURE__*/_jsx(TranslatedFeature, {
                  translated: translated,
                  attribute: "name"
                })
              })
            })
          }, feature.featureId);
        }), featureCouple.length === 1 && /*#__PURE__*/_jsx("td", {})]
      }, featureCouple[0].featureId);
    });
  };
  const groupArrayIntoTwos = array => array.reduce((acc, value, index, fullArray) => {
    if (index % 2 === 0) {
      acc.push(fullArray.slice(index, index + 2));
    }
    return acc;
  }, []);
  const TextKeyBase = hideFreeUsersDisclaimer ? 'seats' : 'pricingPage';
  const productMap = highlightedProducts.reduce((acc, apiName) => {
    const level = getProductLevelFromApiNameString(apiName);
    return Object.assign({}, acc, {
      [level]: apiName
    });
  }, {});
  const getTableFeatures = ({
    level,
    requiresPaidSeat
  }) => {
    const apiName = productMap[level];
    const lowerProductLevel = currentOrderedProductLevels.indexOf(level) > 0 ? currentOrderedProductLevels[currentOrderedProductLevels.indexOf(level) - 1] : null;
    const lowerApiName = lowerProductLevel && productMap[lowerProductLevel];

    // We want to filter the features keeping any that apply to this level,
    // but aren't in the level below.
    // And filter based on requiresPaidSeat input
    const filteredFeatures = features.filter(feature => {
      const levelHasFeature = feature.shortLimits[apiName] || feature.shouldUseDefaultLimits[apiName];
      const lowerLevelHasFeature = lowerApiName && !!(feature.shortLimits[lowerApiName] || feature.shouldUseDefaultLimits[lowerApiName]) || false;
      const requiresSeat = feature.freeUserAccessLevel === API_NO_ACCESS;
      const hideLower = !requiresPaidSeat && lowerLevelHasFeature;
      return levelHasFeature && !hideLower && requiresPaidSeat === requiresSeat;
    });
    const featureDoubles = groupArrayIntoTwos(filteredFeatures);
    return makeFeatureCells({
      featureDoubles,
      apiName
    });
  };
  const paidProductLevels = highlightedProducts.map(apiName => getProductLevelFromApiNameString(apiName)).filter(level => level !== FREE);
  const titleCopyMap = {
    [SALES]: {
      [STARTER]: 'pricingPage.seatsPicker.paidUsersModal.title.SALES.STARTER',
      [PROFESSIONAL]: 'pricingPage.seatsPicker.paidUsersModal.title.SALES.PROFESSIONAL',
      [ENTERPRISE]: 'pricingPage.seatsPicker.paidUsersModal.title.SALES.ENTERPRISE'
    },
    [SERVICE]: {
      [STARTER]: 'pricingPage.seatsPicker.paidUsersModal.title.SERVICE.STARTER',
      [PROFESSIONAL]: 'pricingPage.seatsPicker.paidUsersModal.title.SERVICE.PROFESSIONAL',
      [ENTERPRISE]: 'pricingPage.seatsPicker.paidUsersModal.title.SERVICE.ENTERPRISE'
    }
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(UITabs, {
      defaultSelected: productLevel,
      panelClassName: "p-x-0",
      use: "enclosed",
      children: paidProductLevels.map(level => {
        return /*#__PURE__*/_jsxs(UITab, {
          tabId: level,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: titleCopyMap[productLine][level]
          }),
          children: [/*#__PURE__*/_jsxs(UITable, {
            className: "m-top-6",
            children: [/*#__PURE__*/_jsx("thead", {
              children: /*#__PURE__*/_jsx("tr", {
                children: /*#__PURE__*/_jsxs("th", {
                  colSpan: 2,
                  className: "p-y-4",
                  children: [/*#__PURE__*/_jsx(H6, {
                    className: "m-bottom-2",
                    "aria-level": 3,
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "pricingPage.seatsPicker.paidUsersModal.table.paidUsersOnlyTitle"
                    })
                  }), /*#__PURE__*/_jsx("span", {
                    style: {
                      fontSize: '12px',
                      textTransform: 'initial',
                      fontWeight: 'normal',
                      color: SLINKY
                    },
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "pricingPage.seatsPicker.paidUsersModal.table.paidUsersOnlySubtitle"
                    })
                  })]
                })
              })
            }), /*#__PURE__*/_jsx("tbody", {
              children: getTableFeatures({
                level,
                requiresPaidSeat: true
              })
            })]
          }), /*#__PURE__*/_jsxs(UITable, {
            className: "m-top-6",
            children: [/*#__PURE__*/_jsx("thead", {
              children: /*#__PURE__*/_jsx("tr", {
                children: /*#__PURE__*/_jsxs("th", {
                  colSpan: 2,
                  className: "p-y-4",
                  children: [/*#__PURE__*/_jsx(H6, {
                    className: "m-bottom-2",
                    "aria-level": 3,
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: `${TextKeyBase}.seatsPicker.paidUsersModal.table.freeUsersTitle`
                    })
                  }), /*#__PURE__*/_jsx("span", {
                    style: {
                      fontSize: '12px',
                      textTransform: 'initial',
                      fontWeight: 'normal',
                      color: SLINKY
                    },
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: `${TextKeyBase}.seatsPicker.paidUsersModal.table.freeUsersSubtitle`
                    })
                  })]
                })
              })
            }), /*#__PURE__*/_jsx("tbody", {
              children: getTableFeatures({
                level,
                requiresPaidSeat: false
              })
            })]
          })]
        }, level);
      })
    })
  });
};
export default PaidUsersFeatureTable;